import { Globe } from 'lucide-react';

import { Button } from '@shared/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover';
import { useLanguage, SupportedLanguage } from '@core/i18n';
import { cn } from '@shared/utils/cn';

interface LanguageSelectorProps {
  /** Show full language name instead of code */
  showFullName?: boolean;
  /** Custom class name */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Language selector dropdown component
 * Allows users to switch between supported languages
 */
export function LanguageSelector({
  showFullName = false,
  className,
  size = 'md',
}: LanguageSelectorProps) {
  const { currentLanguage, currentLanguageOption, languages, setLanguage } = useLanguage();

  const sizeClasses = {
    sm: 'text-xs gap-1',
    md: 'text-sm gap-1.5',
    lg: 'text-base gap-2',
  };

  const iconSizes = {
    sm: 14,
    md: 18,
    lg: 22,
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'flex items-center text-brand hover:text-brand/80',
            sizeClasses[size],
            className
          )}
        >
          <Globe size={iconSizes[size]} />
          <span className="uppercase">
            {showFullName ? currentLanguageOption.nativeName : currentLanguage.toUpperCase()}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-40 p-1" align="start">
        <div className="flex flex-col">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code as SupportedLanguage)}
              className={cn(
                'flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                currentLanguage === lang.code && 'bg-accent font-medium'
              )}
            >
              <span>{lang.nativeName}</span>
              <span className="text-muted-foreground text-xs uppercase">
                {lang.code}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default LanguageSelector;
